import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAddToCart,
  useCheckItemAvailability,
  useRemoveFromCart,
} from "@apis/cartRequest";
import { useBulkActions } from "@apis/collection";
import { Diamond } from "@src/types/DiamondDetail";
import { ProductCategory } from "@utils/constants";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { VDBText } from "@components/atoms";
import { CartTypeRequest, getStockItemType } from "../Utils";
import { FavoritesEnum } from "@scenes/favorites/utils/FavoritesUtils";
import { BulkRequestEnum } from "@utils/constants";
import Routes from "@navigations/Routes";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import { ShareVia } from "@components/molecules/Share/utils/utils";
import { CheckItemAvailabilityResponse } from "@src/types/CartItemListResponse";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ShareMenuOptionEnum } from "@utils/product/menuBuilders";
import {
  BulkActionsEnum,
  CartTypeEnum,
  SearchResultsAndFavoritesEnum,
  SearchResultsScreenModals,
} from "@utils/product/searchResultConstants";
import { navigate } from "@navigations/utils/RootNavigation";
import {
  FileFormat,
  ShareMode,
} from "@components/molecules/Share/components/DownloadCSVModal";
import { useCreateExport } from "@apis/exports";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { setLatestExportData } from "@redux/slices/appStateSlice";
import { useSelector } from "react-redux";
import { isPair, isParcel } from "@utils/product/productValidators";
import { CartItemsResult } from "@src/types/CartItemResult";
import {
  useAllowQuantityOnBulk,
  useAllowBMHAndCartActions,
  useAllowJewelryVariationModal,
} from "@redux/slices/globalSlice";

interface UseBulkActionsLogicParams {
  // Context
  productType: ProductCategory;
  stoneType: StoneType;
  priceMode: PriceModes;
  sectionName: string;
  t: any;
  scrollValue: any;
  bottom: number;
  dispatch: any;
  isGuest: boolean;

  // State
  selectedItems: Diamond[];
  itemDetails: any;
  requestItems: any[];
  singleItem: any;
  collectionCountRef: React.MutableRefObject<number>;

  // State setters
  setShowModal: (state: any) => void;
  setOpenBulkDialog: (value: boolean) => void;
  setOpenOrderCartDialog: (value: boolean, isBuyNow?: boolean) => void;
  setShowBMHModal: (value: boolean) => void;
  setSingleItem: (item: any) => void;
  setItemDetails: (details: any) => void;
  setSelectedItems: (items: Diamond[]) => void;

  // Callbacks
  updateFavorite: (isFavorite: boolean, items: Diamond[]) => void;
  getToastBottomMargin: (scrollValue: any, bottom: number) => number;
  handleReset: () => void;
  setLoginModalType: (type: string) => void;
  queryParams: any;
}

export function useBulkActionsLogic({
  productType,
  stoneType,
  priceMode,
  sectionName,
  t,
  scrollValue,
  bottom,
  dispatch,
  isGuest,
  selectedItems,
  itemDetails,
  requestItems,
  singleItem,
  collectionCountRef,
  setShowModal,
  setOpenBulkDialog,
  setOpenOrderCartDialog,
  setShowBMHModal,
  setSingleItem,
  setItemDetails,
  setSelectedItems,
  updateFavorite,
  getToastBottomMargin,
  handleReset,
  setLoginModalType,
  queryParams,
}: UseBulkActionsLogicParams) {
  const queryClient = useQueryClient();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const addToCart = useAddToCart(BulkRequestEnum.FromResultScreen);
  const removeFromCart = useRemoveFromCart(BulkRequestEnum.FromResultScreen);
  const bulkActionsCall = useBulkActions();
  const checkItemAvailability = useCheckItemAvailability();
  const userInfo = useUserInfo();
  const createExport = useCreateExport();
  const allowBMHAndCartActions = useAllowBMHAndCartActions();
  const maxItems = useSelector(
    (state: any) => state.globalData?.app_settings?.maximum_exports_number
  );
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const allowJewelryVariationModal = useAllowJewelryVariationModal();
  const excelSharePayload = useMemo(() => {
    return {
      ...queryParams,
      ...(productType === ProductCategory.LAB_GROWN_DIAMOND && {
        lab_grown: true,
      }),
    };
  }, [queryParams, productType]);
  const addToBulkCart = useCallback(
    (itemIds: Array<number>, isAdded: boolean) => {
      const idSet = new Set(itemIds);
      queryClient.setQueriesData(
        { queryKey: ["SEARCH_RESULTS"] },
        (data: any) => {
          if (!data?.pages) return data;

          let changed = false;
          const newPages = data.pages.map((page: any) => {
            const body = page?.data?.response?.body;
            if (!body) return page;
            const key = body.diamonds
              ? "diamonds"
              : body.gemstones
              ? "gemstones"
              : body.jewelries
              ? "jewelries"
              : null;
            if (!key || !body[key]) return page;

            const items = body[key];
            if (!items.some((it: Diamond) => it?.id && idSet.has(it.id)))
              return page;

            changed = true;
            return {
              ...page,
              data: {
                ...page.data,
                response: {
                  ...page.data.response,
                  body: {
                    ...body,
                    [key]: items.map((it: Diamond) =>
                      it?.id && idSet.has(it.id)
                        ? { ...it, in_cart: isAdded }
                        : it
                    ),
                  },
                },
              },
            };
          });

          return changed ? { ...data, pages: newPages } : data;
        }
      );
    },
    [queryClient]
  );

  const deleteFromBulkRequest = useCallback(
    (ids: Array<number>) => {
      const idSet = new Set(ids);
      queryClient.setQueriesData(
        { queryKey: ["SEARCH_RESULTS"] },
        (data: any) => {
          if (!data?.pages) return data;

          let changed = false;
          const newPages = data.pages.map((page: any) => {
            const body = page?.data?.response?.body;
            if (!body) return page;
            const key = body.diamonds
              ? "diamonds"
              : body.gemstones
              ? "gemstones"
              : body.jewelries
              ? "jewelries"
              : null;
            if (!key || !body[key]) return page;

            const items = body[key];
            if (!items.some((it: Diamond) => it?.id && idSet.has(it.id)))
              return page;

            changed = true;
            return {
              ...page,
              data: {
                ...page.data,
                response: {
                  ...page.data.response,
                  body: {
                    ...body,
                    [key]: items.map((it: Diamond) =>
                      it?.id && idSet.has(it.id)
                        ? { ...it, in_cart: false }
                        : it
                    ),
                  },
                },
              },
            };
          });

          return changed ? { ...data, pages: newPages } : data;
        }
      );
    },
    [queryClient]
  );

  const removeFromBulkRequest = useCallback(
    (id: number, cartType: string) => {
      const stockItemsCart: CartTypeRequest = {
        stock_item_ids: [id],
        cart_type: cartType,
        stock_type: getStockItemType(productType),
        price_mode: priceMode,
      };

      removeFromCart.mutate(
        { stock_items_cart: stockItemsCart },
        {
          onSuccess(data, variables) {
            if (data?.data?.response?.error_code === 0) {
              showToast(
                t("global.removeFromBulkRequest.text"),
                data?.data?.response?.message,
                ToastTypes.SUCCESS,
                true,
                true,
                getToastBottomMargin(scrollValue, bottom)
              );
              if (variables?.stock_items_cart?.stock_item_ids) {
                deleteFromBulkRequest(
                  variables.stock_items_cart.stock_item_ids
                );
              }
              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          },
        }
      );
    },
    [
      productType,
      priceMode,
      removeFromCart,
      deleteFromBulkRequest,
      t,
      scrollValue,
      bottom,
      getToastBottomMargin,
      setShowModal,
    ]
  );

  const addToBulkRequest = useCallback(
    (id: number, cartType: string) => {
      const stockItemsCart: CartTypeRequest = {
        stock_item_id: id,
        cart_type: cartType,
        stock_type: getStockItemType(productType),
        price_mode: priceMode,
        ...((sectionName === SearchFilterTypeEnum.PAIRS ||
          sectionName === SearchFilterTypeEnum.PARCEL) && {
          product_sub_type:
            sectionName === SearchFilterTypeEnum.PAIRS
              ? StoneType.PAIR
              : StoneType.PARCELS,
        }),
      };

      addToCart.mutate(
        { stock_items_cart: stockItemsCart },
        {
          onSuccess(data, variables) {
            if (data?.data?.response?.error_code === 0) {
              showToast(
                t("global.addToBulkRequest.text"),
                t(
                  data?.data?.response?.translation_key ??
                    data?.data?.response?.message
                ),
                ToastTypes.SUCCESS,
                true,
                true,
                getToastBottomMargin(scrollValue, bottom)
              );
              if (variables?.stock_items_cart?.stock_item_id) {
                addToBulkCart([variables.stock_items_cart.stock_item_id], true);
              }
              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            } else if (data?.data?.response?.error_code > 0) {
              showToast(
                "",
                t(
                  data?.data?.response?.translation_key ??
                    data?.data?.response?.error_message
                ),
                ToastTypes.INFO,
                true,
                true,
                getToastBottomMargin(scrollValue, bottom)
              );
              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          },
        }
      );
    },
    [
      productType,
      priceMode,
      sectionName,
      addToCart,
      addToBulkCart,
      t,
      scrollValue,
      bottom,
      getToastBottomMargin,
      setShowModal,
    ]
  );

  const navigateToBulkInitScreen = useCallback(
    (
      requestType: string,
      selectedCartData: any,
      count: number,
      temporaryItem?: any,
      showErrorToast: boolean = false,
      buyNowSessionId?: string,
      notAddedData?: any
    ) => {
      const filterSelectedItem =
        temporaryItem && selectedItems.length === 0
          ? Array.isArray(temporaryItem)
            ? temporaryItem
            : [temporaryItem]
          : selectedItems;

      const filteredItems = filterSelectedItem
        .filter((data) => {
          const match = selectedCartData.some(
            (item) => data.id?.toString() === item.stock_item_id && item?.added
          );
          return match;
        })
        .map((matchedItem: any) => {
          const matchedCartItem = selectedCartData.find(
            (item) => matchedItem.id?.toString() === item.stock_item_id
          );
          return {
            id: matchedCartItem?.stock_items_cart_id,
            stock_item_id: matchedItem?.id,
            item: matchedItem,
            status: matchedItem?.status,
            stock_type: matchedItem?.type,
            quantity: matchedItem?.quantity,
            max_quantity_allowed: matchedItem?.max_quantity_allowed,
            total_price: matchedItem?.total_sales_price,
            old_total_price: matchedItem?.old_total_price,
            unit_price: matchedItem?.total_sales_price,
            old_unit_price: matchedItem?.old_unit_price,
            additional_attributes: matchedItem?.additional_attributes,
            configuration_details: matchedItem?.configuration_details,
          };
        });

      if (count !== 0) {
        let enableToastMessage = false;

        if (
          temporaryItem &&
          [temporaryItem].length === 1 &&
          Object.keys(temporaryItem).length > 1
        ) {
          enableToastMessage = false;
        } else {
          enableToastMessage =
            selectedItems.length > 0 && selectedItems.length !== count;
        }

        if (enableToastMessage) {
          if (
            allowBMHAndCartActions &&
            productType === ProductCategory.JEWELRY &&
            notAddedData?.not_added_due_to_inspirational ===
              selectedItems.length
          ) {
            showToast(
              "",
              t(
                "searchResult.bulkActions.requestItems.noData.inspirational.text",
                "Inspirational products cannot be requested; please use Ask a Question"
              ),
              ToastTypes.ERROR,
              true,
              true
            );
          } else if (
            allowBMHAndCartActions &&
            productType === ProductCategory.JEWELRY &&
            notAddedData?.not_added_due_to_inspirational > 0
          ) {
            showToast(
              "",
              t(
                "searchResult.bulkActions.requestItems.partialData.inspirational.text",
                "Inspirational products have not been added to the request list. Please use Ask a Question"
              ),
              ToastTypes.ERROR,
              true,
              true
            );
          } else {
            if (showErrorToast) {
              showToast(
                "",
                t("searchResult.bulkActions.requestItems.partialData.text", {
                  counts: selectedItems.length - count,
                }),
                ToastTypes.ERROR,
                true,
                true
              );
            } else {
              showToast(
                "",
                t("searchResult.bulkActions.requestItems.partialData.text", {
                  counts: selectedItems.length - count,
                }),
                ToastTypes.INFO,
                true,
                true
              );
            }
          }
        }

        handleReset();
        setSingleItem({});
        setSelectedItems([]);
        navigate(Routes.BulkRequestNavigator, {
          screen: Routes.BulkInitScreen,
          params: {
            initial: false,
            productType: productType,
            stoneType: stoneType ?? StoneType.SINGLE_STONES,
            itemArray: buyNowSessionId ? [] : filteredItems,
            requestType,
            isRequestNowType: true,
            ...(buyNowSessionId && { buy_now_session_id: buyNowSessionId }),
          },
        });
      } else {
        if (productType === ProductCategory.JEWELRY && allowBMHAndCartActions) {
          showToast(
            "",
            t(
              "searchResult.bulkActions.requestItems.noData.inspirational.text",
              "Inspirational products cannot be requested; please use Ask a Question"
            ),
            ToastTypes.ERROR,
            true,
            true
          );
        } else {
          showToast(
            "",
            t("searchResult.bulkActions.requestItems.noData.text"),
            ToastTypes.ERROR,
            true,
            true
          );
        }
      }
    },
    [
      selectedItems,
      t,
      handleReset,
      setSingleItem,
      setSelectedItems,
      productType,
    ]
  );
  const sendEmailExcelData = useCallback(
    (ids: Array<number>, emailData: any) => {
      bulkActionsCall.mutate(
        {
          action_type: "email_searched_items",
          item_ids: ids?.length > 0 ? ids : null,
          stock_type: productType,
          price_mode: priceMode,
          query: JSON.stringify({ vdb: excelSharePayload }),
          send_to: emailData.emailAddress,
          cc: emailData.ccList,
          comments: emailData.comments,
          subject: emailData.subject,
          ...((sectionName === SearchFilterTypeEnum.PAIRS ||
            sectionName === SearchFilterTypeEnum.PARCEL) && {
            product_sub_type:
              sectionName === SearchFilterTypeEnum.PAIRS
                ? StoneType.PAIR
                : StoneType.PARCELS,
          }),
        },
        {
          onSuccess(data) {
            if (data?.data?.response?.header?.status === 200) {
              setTimeout(() => {
                showToast(
                  "Done",
                  "The email will be sent as soon as the files are generated.",
                  ToastTypes.SUCCESS,
                  true,
                  true
                );
              }, 1000);

              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          },
        }
      );
    },
    [bulkActionsCall, productType, priceMode, sectionName]
  );

  const DownloadCSVData = useCallback(
    (
      ids: Array<number>,
      format: FileFormat,
      actionType: ShareMode,
      selectedItemsCount: number
    ) => {
      createExport.mutate(
        {
          action_type: actionType,
          item_ids: ids?.length > 0 ? ids : [],
          stock_type: productType,
          price_mode: priceMode,
          query: JSON.stringify({ vdb: excelSharePayload }),
          file_format: format,
          max_items: maxItems ?? 10000,
          total_items: selectedItemsCount,
          ...((sectionName === SearchFilterTypeEnum.PAIRS ||
            sectionName === SearchFilterTypeEnum.PARCEL) && {
            product_sub_type:
              sectionName === SearchFilterTypeEnum.PAIRS
                ? StoneType.PAIR
                : StoneType.PARCELS,
          }),
        },
        {
          onSuccess(data) {
            if (data?.data?.success) {
              dispatch(setLatestExportData(data?.data?.export));

              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          },
        }
      );
    },
    [createExport, productType, priceMode, sectionName]
  );

  const addMultipleItemsToBulk = useCallback(
    (ids: Array<number>, cartType?: CartTypeEnum, isBuyNow?: boolean) => {
      bulkActionsCall.mutate(
        {
          action_type: BulkActionsEnum.BULK_REQUEST_ACTION,
          item_ids: ids,
          price_mode: priceMode,
          stock_type: productType,
          ...(cartType && {
            cart_type: cartType,
          }),
          ...(isBuyNow && {
            buy_now: isBuyNow,
          }),
          ...((sectionName === SearchFilterTypeEnum.PAIRS ||
            sectionName === SearchFilterTypeEnum.PARCEL) && {
            product_sub_type:
              sectionName === SearchFilterTypeEnum.PAIRS
                ? StoneType.PAIR
                : StoneType.PARCELS,
          }),
        },
        {
          onSuccess(data) {
            if (data?.data?.response?.header?.status === 200) {
              const body = data?.data?.response?.body;
              const message = data?.data?.response?.header?.message;
              addToBulkCart(ids, true);
              if (message?.length > 0 && !cartType) {
                showToast("", message, ToastTypes.SUCCESS, true, true);
              }
              if (cartType) {
                const notAddedData = body?.not_added_data;
                if (notAddedData?.not_added_due_to_is_customizable > 0) {
                  showToast(
                    "",
                    `(${notAddedData?.not_added_due_to_is_customizable})` +
                      t(
                        "global.addToCart.message.text",
                        "item(s) could not be added due to technical limitations "
                      ),
                    ids?.length ===
                      notAddedData?.not_added_due_to_is_customizable
                      ? ToastTypes.ERROR
                      : ToastTypes.SUCCESS,
                    true,
                    true
                  );
                } else if (!isBuyNow) {
                  showToast("", message, ToastTypes.SUCCESS, true, true);
                }
                if (
                  isBuyNow &&
                  ids?.length !== notAddedData?.not_added_due_to_is_customizable
                ) {
                  navigateToOrderCartScreen(data?.data);
                }
              }
              setShowModal({
                name: null,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          },
          onSettled() {
            setShowModal({
              name: null,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
          },
        }
      );
    },
    [
      addToBulkCart,
      bulkActionsCall,
      priceMode,
      productType,
      sectionName,
      setShowModal,
      t,
    ]
  );

  const checkItemAvailabilityForBulk = (
    ids: Array<number>,
    onSuccess: (data: CheckItemAvailabilityResponse) => void
  ) => {
    checkItemAvailability.mutate(
      { stock_item_ids: ids },
      {
        onSuccess(data) {
          let response: CheckItemAvailabilityResponse = data?.data;
          if (response?.status_code === 1) {
            onSuccess(response);
          } else if (response?.status_code === 0) {
            setShowModal({
              name: null,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
            showToast(
              "",
              t("productDetail.addToCart.unavailable.text"),
              ToastTypes.ERROR,
              true,
              true
            );
          } else if (response?.status_code === 2) {
            onSuccess(response);
          }
        },
      }
    );
  };

  const processBulkRequest = useCallback(
    (selectOptionName: string, temporaryItem?: Diamond) => {
      setShowModal({
        name: SearchResultsScreenModals.LOADER_MODAL,
        selectedOptions: null,
        shareMenuOption: null,
        openBulkDialog: false,
      });

      let arr3 = [];
      if (temporaryItem && Object.keys(temporaryItem).length > 0) {
        arr3.push(temporaryItem?.id ?? 0);
      } else {
        arr3 = selectedItems.map((item) => item?.id ?? 0);
      }

      const isInstantRequest = [
        SearchResultsAndFavoritesEnum.REQUEST_ITEMS,
        SearchResultsAndFavoritesEnum.BUY_ITEMS,
        SearchResultsAndFavoritesEnum.MEMO_ITEMS,
        SearchResultsAndFavoritesEnum.HOLD_ITEMS,
      ].includes(selectOptionName.toString() as SearchResultsAndFavoritesEnum);

      const isAddToBulkList =
        selectOptionName === SearchResultsAndFavoritesEnum.ADD_TO_BULK_LIST;
      const requestedItemIds = arr3
        .map((id) => Number(id))
        .filter((id) => id > 0);

      bulkActionsCall.mutate(
        {
          action_type: FavoritesEnum.bulk_request,
          item_ids: arr3,
          price_mode: priceMode,
          stock_type: productType,
          ...(isInstantRequest && {
            cart_type: BulkActionsEnum.REQUEST_NOW_ACTION,
          }),
          ...(allowQuantityOnBulk &&
            productType === ProductCategory.JEWELRY &&
            isInstantRequest && {
              buy_now: true,
            }),
          ...((sectionName === SearchFilterTypeEnum.PAIRS ||
            sectionName === SearchFilterTypeEnum.PARCEL) && {
            product_sub_type:
              sectionName === SearchFilterTypeEnum.PAIRS
                ? StoneType.PAIR
                : StoneType.PARCELS,
          }),
        },
        {
          onSuccess(data) {
            if (data?.data?.response?.header?.status === 200) {
              const result = data?.data?.response?.body?.result;
              if (
                data?.data?.response?.body?.result?.length > 0 &&
                !isInstantRequest
              ) {
                if (
                  allowBMHAndCartActions &&
                  productType === ProductCategory.JEWELRY &&
                  data?.data?.response?.body?.not_added_data
                    ?.not_added_due_to_inspirational === selectedItems.length
                ) {
                  showToast(
                    "",
                    t(
                      "searchResult.bulkActions.requestItems.noData.inspirational.text",
                      "Inspirational products cannot be requested; please use Ask a Question"
                    ),
                    ToastTypes.ERROR,
                    true,
                    true
                  );
                } else if (
                  allowBMHAndCartActions &&
                  productType === ProductCategory.JEWELRY &&
                  data?.data?.response?.body?.not_added_data
                    ?.not_added_due_to_inspirational > 0
                ) {
                  showToast(
                    "",
                    t(
                      "searchResult.bulkActions.requestItems.partialData.inspirational.text",
                      "Inspirational products have not been added to the request list. Please use Ask a Question"
                    ),
                    ToastTypes.ERROR,
                    true,
                    true
                  );
                } else {
                  showToast(
                    "",
                    t("global.addToBulkRequest.message.text", {
                      count: data?.data?.response?.body?.result?.length,
                      multiple:
                        data?.data?.response?.body?.result?.length > 1
                          ? "(s)"
                          : "",
                    }),
                    ToastTypes.SUCCESS,
                    true,
                    true
                  );
                }
              }
              const resultItemIds =
                result && Array.isArray(result)
                  ? result
                      .map((_stockItem) => Number(_stockItem?.stock_item_id))
                      .filter((id) => id > 0)
                  : [];

              const inCartItemIds = isAddToBulkList
                ? resultItemIds.length > 0
                  ? resultItemIds
                  : requestedItemIds
                : resultItemIds;

              if (inCartItemIds.length > 0 && !isInstantRequest) {
                addToBulkCart(inCartItemIds, true);
              }
              if (isInstantRequest) {
                navigateToBulkInitScreen(
                  selectOptionName.toString(),
                  result,
                  data?.data?.response?.body?.count,
                  temporaryItem,
                  data?.data?.response?.body
                    ?.not_requested_due_to_status_count > 0 &&
                    userInfo?.restrictActionOnUnavailableItems()
                    ? true
                    : false,
                  data?.data?.response?.body?.buy_now_session_id,
                  data?.data?.response?.body?.not_added_data
                );
              }
            }
          },
          onSettled() {
            setShowModal({
              name: null,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
          },
        }
      );
    },
    [
      selectedItems,
      priceMode,
      productType,
      sectionName,
      bulkActionsCall,
      t,
      addToBulkCart,
      navigateToBulkInitScreen,
      setShowModal,
      allowQuantityOnBulk,
    ]
  );

  const addToCartMutation = useCallback(
    (
      item: Diamond,
      cartType: CartTypeEnum,
      type: ProductCategory,
      isBuyNow: boolean = false
    ) => {
      const stockItemsCart: CartTypeRequest = {
        stock_item_id: item.id,
        cart_type: cartType,
        stock_type: getStockItemType(productType as ProductCategory),
        ...(isBuyNow && {
          buy_now: isBuyNow,
        }),
        price_mode: priceMode,
        ...((isPair(item) || (isCustomApp && isParcel(item))) && {
          product_sub_type: isPair(item) ? StoneType.PAIR : StoneType.PARCELS,
        }),
      };

      addToCart.mutate(
        {
          stock_items_cart: stockItemsCart,
        },
        {
          onSuccess(data) {
            setShowModal({
              name: null,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
            if (data?.data?.response?.error_code === 0) {
              if (item?.id) {
                addToBulkCart([item.id], true);
              }
              if (!isBuyNow) {
                showToast(
                  "",
                  t(
                    "productDetail.addToCart.success.text",
                    data?.data?.response?.message
                  ),
                  ToastTypes.SUCCESS,
                  true,
                  true,
                  80
                );
              }
              if (isBuyNow) {
                navigateToOrderCartScreen(data?.data);
              }
              queryClient
                .invalidateQueries({ queryKey: ["GET_CART_ITEM"] })
                .then();
            } else if (data?.data?.response?.error_code > 0) {
              if (data?.data?.response?.error_code === 13) {
                showToast(
                  "",
                  t(
                    "productDetail.addToCart.maxQuantity.text",
                    "Entered quantity is out of availability"
                  ),
                  ToastTypes.INFO,
                  true,
                  true,
                  80
                );
              } else {
                showToast(
                  "",
                  data?.data?.response?.error_message,
                  ToastTypes.INFO,
                  true,
                  true,
                  80
                );
              }
            }
          },
        }
      );
    },
    [
      addToBulkCart,
      addToCart,
      isCustomApp,
      priceMode,
      productType,
      queryClient,
      setShowModal,
      t,
    ]
  );

  const navigateToOrderCartScreen = (response: CartItemsResult) => {
    navigate(Routes.BulkRequestNavigator, {
      screen: Routes.OrderCartScreen,
      params: {
        isBuyNow: true,
        buyNowSessionId:
          response?.response.buy_now_session_id ??
          response?.response?.body?.buy_now_session_id,
        productSubType: response?.response.product_sub_type,
      },
    });
  };

  const bulkActionCallback = useCallback(
    (selectedOptionName: string) => {
      switch (selectedOptionName) {
        case SearchResultsAndFavoritesEnum.SHARE_EMAIL:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: SearchResultsScreenModals.SHARE,
              selectedOptions: ShareVia.EMAIL,
              shareMenuOption: ShareMenuOptionEnum.MULTIPLE,
              openBulkDialog: false,
            });
          }
          break;

        case SearchResultsAndFavoritesEnum.REQUEST_ITEMS:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: null,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
            setSingleItem({});
            if (requestItems?.length === 1) {
              setShowBMHModal(false);
              processBulkRequest(requestItems[0]?.name);
            } else {
              setShowBMHModal(true);
            }
          }
          break;

        case SearchResultsAndFavoritesEnum.BUY_ITEMS:
        case SearchResultsAndFavoritesEnum.MEMO_ITEMS:
        case SearchResultsAndFavoritesEnum.HOLD_ITEMS:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowBMHModal(false);
            if (userInfo?.restrictActionOnUnavailableItems()) {
              checkItemAvailabilityForBulk(
                selectedItems.map((item) => Number(item?.id) || 0),
                (data: CheckItemAvailabilityResponse) => {
                  if (data?.status_code === 1 || data?.status_code === 2) {
                    processBulkRequest(selectedOptionName, singleItem);
                  }
                }
              );
            } else {
              processBulkRequest(selectedOptionName, singleItem);
            }
          }
          break;

        case SearchResultsAndFavoritesEnum.SHARE_NATIVE:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: SearchResultsScreenModals.SHARE,
              selectedOptions: ShareVia.NATIVE,
              shareMenuOption: ShareMenuOptionEnum.MULTIPLE,
              openBulkDialog: false,
            });
          }
          break;

        case SearchResultsAndFavoritesEnum.ADD_TO_COLLECTION:
          const arr2 = selectedItems.map((item) => item?.id ?? 0);
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setItemDetails({
              mItem: arr2.length === 1 ? selectedItems[0] : itemDetails?.mItem,
              itemIdArray: arr2,
            });
            if (collectionCountRef.current > 0) {
              setShowModal({
                name: SearchResultsScreenModals.COLLECTIONS_LIST,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            } else {
              setShowModal({
                name: SearchResultsScreenModals.CREATE_NEW_COLLECTION,
                selectedOptions: null,
                shareMenuOption: null,
                openBulkDialog: false,
              });
            }
          }
          break;

        case SearchResultsAndFavoritesEnum.ADD_TO_FAVORITES:
          const favArr = selectedItems.map((item) => item?.id ?? 0);
          setItemDetails({
            mItem: favArr.length === 1 ? selectedItems[0] : itemDetails?.mItem,
            itemIdArray: favArr,
          });
          setOpenBulkDialog(false);
          setOpenOrderCartDialog(false);
          bulkActionsCall.mutate(
            {
              action_type: FavoritesEnum.add_favorites,
              item_ids: favArr,
              price_mode: priceMode,
              stock_type: productType,
              ...((sectionName === SearchFilterTypeEnum.PAIRS ||
                sectionName === SearchFilterTypeEnum.PARCEL) && {
                product_sub_type:
                  sectionName === SearchFilterTypeEnum.PAIRS
                    ? StoneType.PAIR
                    : StoneType.PARCELS,
              }),
            },
            {
              onSuccess(data) {
                setOpenBulkDialog(false);
                setOpenOrderCartDialog(false);
                updateFavorite(true, selectedItems);
                if (data?.data?.response?.header?.status === 200) {
                  showToast(
                    `${t("global.text.done")}!`,
                    React.createElement(
                      VDBText,
                      {},
                      `${favArr.length} ${t(
                        "searchResult.addMultipleItemsToFavorites.text"
                      )}`
                    ),
                    ToastTypes.SUCCESS,
                    true,
                    true,
                    getToastBottomMargin(scrollValue, bottom)
                  );
                } else {
                  showToast(
                    "",
                    React.createElement(
                      VDBText,
                      {},
                      data?.data?.response?.header?.message
                    ),
                    data?.data?.response?.header?.status === 422
                      ? ToastTypes.ERROR
                      : ToastTypes.SUCCESS,
                    true,
                    true,
                    getToastBottomMargin(scrollValue, bottom)
                  );
                }
              },
            }
          );
          break;

        case SearchResultsAndFavoritesEnum.ADD_TO_BULK_LIST:
          if (userInfo?.restrictActionOnUnavailableItems()) {
            checkItemAvailabilityForBulk(
              selectedItems.map((item) => Number(item?.id) || 0),
              (data: CheckItemAvailabilityResponse) => {
                if (data?.status_code === 1 || data?.status_code === 2) {
                  processBulkRequest(selectedOptionName);
                }
              }
            );
          } else {
            processBulkRequest(selectedOptionName);
          }
          break;

        case SearchResultsAndFavoritesEnum.SHARE_EMAIL_EXCEL_PDF:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: SearchResultsScreenModals.EMAIL_EXCEL_MODAL,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
          }
          break;
        case SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF:
          if (isGuest) {
            setOpenBulkDialog(false);
            setOpenOrderCartDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: SearchResultsScreenModals.DOWNLOAD_CSV_PDF,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
            });
          }
          break;
        case SearchResultsAndFavoritesEnum.ADD_TO_CART:
          if (isGuest) {
            setOpenBulkDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            if (
              allowJewelryVariationModal &&
              selectedItems?.length === 1 &&
              selectedItems?.[0]?.type === ProductCategory.JEWELRY &&
              selectedItems?.[0].custom_design_configuration === true &&
              selectedItems?.[0]?.display_configuration_v2 &&
              selectedItems?.[0].display_configuration_v2?.length > 0
            ) {
              setOpenOrderCartDialog(true, false);
            } else {
              const cartType = userInfo?.enableShoppingCart()
                ? CartTypeEnum.SHOPPING_CART_TYPE
                : CartTypeEnum.ORDER_CART;
              addMultipleItemsToBulk(
                selectedItems.map((item) => Number(item?.id) || 0),
                cartType,
                false
              );
            }
          }
          break;
        case SearchResultsAndFavoritesEnum.BUY_NOW:
          if (isGuest) {
            setOpenBulkDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            if (
              allowJewelryVariationModal &&
              selectedItems?.length === 1 &&
              selectedItems?.[0]?.type === ProductCategory.JEWELRY &&
              selectedItems?.[0].custom_design_configuration === true &&
              selectedItems?.[0]?.display_configuration_v2 &&
              selectedItems?.[0].display_configuration_v2?.length > 0
            ) {
              setOpenOrderCartDialog(true, true);
            } else {
              const cartType = userInfo?.enableShoppingCart()
                ? CartTypeEnum.SHOPPING_CART_TYPE
                : CartTypeEnum.ORDER_CART;
              addMultipleItemsToBulk(
                selectedItems.map((item) => Number(item?.id) || 0),
                cartType,
                true
              );
            }
          }
          break;
        case SearchResultsAndFavoritesEnum.SHARE_VIA_LINK:
          if (isGuest) {
            setOpenBulkDialog(false);
            dispatch(setLoginModalType("default"));
          } else {
            setShowModal({
              name: SearchResultsScreenModals.SHARE,
              selectedOptions: ShareVia.LINK,
              shareMenuOption: ShareMenuOptionEnum.MULTIPLE,
              openBulkDialog: false,
            });
          }
          break;
      }
    },
    [
      isGuest,
      selectedItems,
      itemDetails,
      requestItems,
      singleItem,
      priceMode,
      productType,
      sectionName,
      collectionCountRef,
      t,
      scrollValue,
      bottom,
      dispatch,
      setOpenBulkDialog,
      setOpenOrderCartDialog,
      setShowModal,
      setSingleItem,
      setShowBMHModal,
      setItemDetails,
      processBulkRequest,
      bulkActionsCall,
      updateFavorite,
      getToastBottomMargin,
      setLoginModalType,
    ]
  );

  return {
    bulkActionCallback,
    addToBulkRequest,
    removeFromBulkRequest,
    processBulkRequest,
    sendEmailExcelData,
    addMultipleItemsToBulk,
    DownloadCSVData,
    isAddingToCart: addToCart.isPending,
    isRemovingFromCart: removeFromCart.isPending,
    isBulkActionPending: bulkActionsCall.isPending,
    isLoading:
      addToCart.isPending ||
      removeFromCart.isPending ||
      bulkActionsCall.isPending,
    checkItemAvailabilityForBulk,
    addToCartMutation,
  };
}
